import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { AUDIENCES } from "../../lib/site-data";

export default function AudienceTabs() {
  const [active, setActive] = useState(AUDIENCES[0].id);
  const audience = AUDIENCES.find((a) => a.id === active);

  return (
    <section
      id="audience"
      data-testid="audience-section"
      className="relative bg-ink py-24 lg:py-32 text-cream"
    >
      <div className="container-x">
        <div className="max-w-3xl mb-12 lg:mb-16">
          <p className="text-[12.5px] font-semibold uppercase tracking-[0.18em] text-clay">
            Built for everyone in the room
          </p>
          <h2 className="mt-4 font-display text-cream text-[36px] sm:text-[44px] lg:text-[56px] font-extrabold leading-[1.05] tracking-tight text-balance">
            Transforming virtual care into a catalyst for better health.
          </h2>
        </div>

        <div
          role="tablist"
          aria-label="Choose audience"
          className="flex flex-wrap gap-2 mb-10"
        >
          {AUDIENCES.map((a) => (
            <button
              key={a.id}
              role="tab"
              aria-selected={active === a.id}
              data-testid={`audience-tab-${a.id}`}
              onClick={() => setActive(a.id)}
              className={`rounded-full px-5 py-2.5 text-[14.5px] font-semibold transition-all ${
                active === a.id
                  ? "bg-cream text-ink shadow-md"
                  : "bg-cream/10 text-cream/80 hover:bg-cream/15 hover:text-cream"
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={audience.id}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
            data-testid={`audience-panel-${audience.id}`}
          >
            <div className="lg:col-span-6 order-2 lg:order-1">
              <h3 className="font-display text-cream text-[28px] sm:text-[36px] lg:text-[44px] font-extrabold leading-[1.07] tracking-tight text-balance">
                {audience.title}
              </h3>
              <p className="mt-5 text-[16.5px] text-cream/80 leading-relaxed max-w-xl">
                {audience.body}
              </p>
              <ul className="mt-7 space-y-3">
                {audience.points.map((p) => (
                  <li
                    key={p}
                    className="flex items-start gap-3 text-[15.5px] text-cream/90"
                  >
                    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-clay/20 text-clay">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>
              <div className="mt-9">
                {audience.cta.href.startsWith("/") ? (
                  <Link
                    to={audience.cta.href}
                    data-testid={`audience-cta-${audience.id}`}
                    className="inline-flex items-center gap-2 rounded-full bg-clay hover:bg-clayHover text-cream px-6 py-3 text-[15px] font-semibold shadow-md hover:-translate-y-0.5 transition-all"
                  >
                    {audience.cta.label}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : (
                  <a
                    href={audience.cta.href}
                    target={audience.cta.external ? "_blank" : undefined}
                    rel={audience.cta.external ? "noopener noreferrer" : undefined}
                    data-testid={`audience-cta-${audience.id}`}
                    className="inline-flex items-center gap-2 rounded-full bg-clay hover:bg-clayHover text-cream px-6 py-3 text-[15px] font-semibold shadow-md hover:-translate-y-0.5 transition-all"
                  >
                    {audience.cta.label}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                )}
              </div>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="absolute -inset-4 -z-10 rounded-[36px] bg-clay/15"
                  style={{ transform: "rotate(-1.5deg)" }}
                />
                <img
                  src={audience.image}
                  alt={audience.image_alt}
                  loading="lazy"
                  className={`block w-full rounded-3xl bg-cream ${
                    audience.imageFit === "contain" ? "object-contain" : "object-cover"
                  } ${
                    audience.imageAspect === "3/2" ? "aspect-[3/2]" : "aspect-[5/4]"
                  }`}
                />
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
